/**
 * Tenant theme — two colours injected at runtime as CSS variables.
 * Ink colours are computed with YIQ so light and dark secondaries both work.
 */

const DEFAULT_PRIMARY = "#c8102e";
const DEFAULT_SECONDARY = "#f4f3f1";

function hexToRgb(hex) {
  const s = (hex || "").replace("#", "").trim();
  if (s.length === 3) {
    return [parseInt(s[0] + s[0], 16), parseInt(s[1] + s[1], 16), parseInt(s[2] + s[2], 16)];
  }
  if (s.length !== 6) return [0, 0, 0];
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
}

const lum = (hex) => {
  const [r, g, b] = hexToRgb(hex).map((c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// WCAG: "light" when dark text (#111) contrasts better than white on this colour.
export function isLight(hex) {
  const L = lum(hex);
  return (L + 0.05) / (0.0056 + 0.05) > 1.05 / (L + 0.05);
}

const validHex = (h) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test((h || "").trim());

export function applyTheme({ primary, secondary } = {}) {
  const p = validHex(primary) ? primary : DEFAULT_PRIMARY;
  const s = validHex(secondary) ? secondary : DEFAULT_SECONDARY;
  const sLight = isLight(s);
  const root = document.documentElement;
  const set = (k, v) => root.style.setProperty(k, v);

  set("--p", p);
  set("--s", s);
  set("--p-ink", isLight(p) ? "#111111" : "#ffffff");
  set("--s-ink", sLight ? "#111111" : "#ffffff");
  root.dataset.sTone = sLight ? "light" : "dark";

  // Surfaces painted with --s (sidebar, badges, bands) read these.
  set("--s-muted", sLight ? "rgba(17,17,17,0.55)" : "rgba(255,255,255,0.55)");
  set("--s-rule", sLight ? "rgba(17,17,17,0.12)" : "rgba(255,255,255,0.08)");
  set("--s-hover", sLight ? "rgba(17,17,17,0.05)" : "rgba(255,255,255,0.07)");
  set("--s-active", sLight ? "rgba(17,17,17,0.08)" : "rgba(0,0,0,0.35)");
  set("--sidebar-fg", sLight ? "#3a3836" : "#c7c3c0");
  set("--sidebar-fg-active", sLight ? "#111111" : "#ffffff");
  set("--sidebar-hover", sLight ? "rgba(17,17,17,0.05)" : "rgba(255,255,255,0.07)");

  // Mirror into legacy --accent so older components still look correct.
  set("--accent", p);
  set("--accent-hover", `color-mix(in srgb, ${p} 86%, #000)`);
  set("--accent-ring", `color-mix(in srgb, ${p} 35%, transparent)`);
  set("--accent-700", `color-mix(in srgb, ${p} 75%, #000)`);
  set("--accent-600", `color-mix(in srgb, ${p} 88%, #000)`);
  set("--accent-300", `color-mix(in srgb, ${p} 35%, #fff)`);
  set("--accent-100", `color-mix(in srgb, ${p} 10%, #fff)`);
}

export const DEMO_THEME = { primary: DEFAULT_PRIMARY, secondary: DEFAULT_SECONDARY };
